import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Megaphone, Users as UsersIcon, User as UserIcon, Trash2, Folder, ArrowLeft, Menu, Camera, Send, Bookmark, Smile } from 'lucide-react';
import useAuthStore from '../../store/useAuthStore';
import useChatStore from '../../store/useChatStore';
import chatApi from '../../services/chatApi';
import chatWebsocket from '../../services/chatWebsocket';
import { dialogKey } from '../../utils/chatDialogUtils';
import CreateChannelModal from './CreateChannelModal';
import CreateGroupModal from './CreateGroupModal';
import ChannelManagementModal from './ChannelManagementModal';
import GroupManagementModal from './GroupManagementModal';
import './IntegratedChatScreen.css';
import { useToast } from '../ToastProvider';
import PhotoLightbox from '../PhotoLightbox';
import SharedCardBubble from './SharedCardBubble';
import StickerPicker from './StickerPicker';

const IntegratedChatScreen = () => {
  const { user, token, updateUser } = useAuthStore();
  const userId = user?.userId ?? user?.id;
  const { channelId, groupId } = useParams();
  const navigate = useNavigate();

  const dialogs = useChatStore((s) => s.dialogs);
  const groups = useChatStore((s) => s.groups);
  const channels = useChatStore((s) => s.channels);
  const users = useChatStore((s) => s.users);
  const stickerPacks = useChatStore((s) => s.stickerPacks);
  const usersLoading = useChatStore((s) => s.usersLoading);
  const dialogsLoading = useChatStore((s) => s.dialogsLoading);
  const dialogsRefreshing = useChatStore((s) => s.dialogsRefreshing);
  const fetchDialogs = useChatStore((s) => s.fetchDialogs);
  const fetchGroups = useChatStore((s) => s.fetchGroups);
  const fetchChannels = useChatStore((s) => s.fetchChannels);
  const fetchUsers = useChatStore((s) => s.fetchUsers);
  const fetchStickerPacks = useChatStore((s) => s.fetchStickerPacks);
  const markDialogReadInStore = useChatStore((s) => s.markDialogRead);
  const bumpDialogOnOutgoing = useChatStore((s) => s.bumpDialogOnOutgoing);
  const setActiveDialogKey = useChatStore((s) => s.setActiveDialogKey);
  const removeDialogFromStore = useChatStore((s) => s.removeDialog);
  const revalidateOnChatOpen = useChatStore((s) => s.revalidateOnChatOpen);

  const loading = dialogsLoading && dialogs.length === 0;

  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showUserList, setShowUserList] = useState(true);
  const [viewMode, setViewMode] = useState('dialogs'); // 'dialogs' или 'contacts'
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [botCommands, setBotCommands] = useState([]);
  const [activeReplyKeyboard, setActiveReplyKeyboard] = useState(null);
  const [isReplyKeyboardOpen, setIsReplyKeyboardOpen] = useState(true);
  const [showCreateChannelModal, setShowCreateChannelModal] = useState(false);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showChannelManagementModal, setShowChannelManagementModal] = useState(false);
  const [showGroupManagementModal, setShowGroupManagementModal] = useState(false);
  const [messageReactions, setMessageReactions] = useState({}); // { messageId: { emoji: [users] } }
  const [showReactionPicker, setShowReactionPicker] = useState(null); // messageId или null
  const [forwardingMessage, setForwardingMessage] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null); // { id, text }
  const [contextMenu, setContextMenu] = useState(null);       // { x, y, message }
  const [contextMenuReactionOpen, setContextMenuReactionOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressActivatedRef = useRef(false);
  const touchPosRef = useRef({ x: 0, y: 0 });
  const contextMenuRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const { showToast, confirm } = useToast();

  // Нормализация сообщений для обработки forwarded, reply и других типов
  const normalizeMessage = (msg) => {
    return {
      id: msg.id,
      content: msg.content,
      sender_id: msg.senderId || msg.sender_id,
      receiver_id: msg.receiverId || msg.receiver_id,
      group_id: msg.groupId || msg.group_id,
      channel_id: msg.channelId || msg.channel_id,
      created_at: msg.createdAt || msg.created_at,
      is_read: msg.isRead !== undefined ? msg.isRead : msg.is_read,
      read_at: msg.readAt || msg.read_at,
      message_type: msg.messageType || msg.message_type || 'text',
      image_data: msg.imageData || msg.image_data,
      audio_data: msg.audioData || msg.audio_data,
      forwarded_from_id: msg.forwardedFromId || msg.forwarded_from_id,
      original_sender_id: msg.originalSenderId || msg.original_sender_id,
      in_reply_to_id: msg.inReplyToId || msg.in_reply_to_id,
      is_pinned: msg.isPinned !== undefined ? msg.isPinned : msg.is_pinned,
      sender: msg.sender ? {
        username: msg.sender.username,
        display_name: msg.sender.displayName || msg.sender.display_name,
        avatar: msg.sender.avatar,
      } : null,
      original_sender: msg.originalSender || msg.original_sender ? {
        username: (msg.originalSender || msg.original_sender).username,
        display_name: (msg.originalSender || msg.original_sender).displayName || (msg.originalSender || msg.original_sender).display_name,
        avatar: (msg.originalSender || msg.original_sender).avatar,
      } : null,
      reply_to: (() => {
        const rt = msg.replyTo || msg.reply_to;
        if (!rt) return null;
        return {
          id: rt.id,
          senderName: rt.senderName || rt.sender?.displayName || rt.sender?.display_name || rt.sender?.username,
          // сервер возвращает либо `text`, либо `content` — нормализуем в оба поля
          text: rt.text || rt.content,
          content: rt.content || rt.text,
        };
      })(),
      reply_markup: msg.replyMarkup || msg.reply_markup || null,
      edited_at: msg.editedAt || msg.edited_at || null
    };
  };

  const formatDate = (date) => {
    const today = new Date();
    const messageDate = new Date(date);
    if (messageDate.toDateString() === today.toDateString()) {
      return 'Сегодня';
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    }
    return messageDate.toLocaleDateString('ru-RU');
  };

  const addMessage = React.useCallback((messageData) => {
    const normalizedMsg = normalizeMessage(messageData);
    const newMessage = {
      id: normalizedMsg.id,
      text: normalizedMsg.content,
      sender: normalizedMsg.sender_id === currentUser?.id ? 'outgoing' : 'incoming',
      senderName: normalizedMsg.sender?.display_name || normalizedMsg.sender?.displayName || normalizedMsg.sender?.full_name || normalizedMsg.sender?.username,
      senderAvatar: normalizedMsg.sender?.avatar,
      time: new Date(normalizedMsg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: formatDate(new Date(normalizedMsg.created_at)),
      messageType: normalizedMsg.message_type,
      image: normalizedMsg.image_data,
      audio: normalizedMsg.audio_data,
      isRead: normalizedMsg.is_read,
      readAt: normalizedMsg.read_at,
      forwardedFromId: normalizedMsg.forwarded_from_id,
      originalSender: normalizedMsg.original_sender,
      inReplyToId: normalizedMsg.in_reply_to_id,
      replyTo: normalizedMsg.reply_to,
      isPinned: normalizedMsg.is_pinned,
      replyMarkup: normalizedMsg.reply_markup
    };

    setMessages(prev => {
      const hasDuplicate = prev.some(m => m.id === newMessage.id);
      if (hasDuplicate) return prev;
      return [...prev, newMessage];
    });
  }, [currentUser]);

  // ReplyKeyboardMarkup (keyboard) показываем как "клавиатуру под инпутом" для текущего диалога
  useEffect(() => {
    if (!selectedUser) {
      setActiveReplyKeyboard(null);
      return;
    }

    // Ищем последнее сообщение в текущем диалоге, где есть replyMarkup.keyboard
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      const rm = m?.replyMarkup;
      if (!rm) continue;

      // Если бот попросил убрать клавиатуру
      if (rm.remove_keyboard) {
        setActiveReplyKeyboard(null);
        return;
      }

      if (Array.isArray(rm.keyboard)) {
        setActiveReplyKeyboard(rm);
        return;
      }
    }

    setActiveReplyKeyboard(null);
  }, [messages, selectedUser]);

  // Если клавиатуры нет — скрываем панель (но позволяем пользователю вручную открыть позже, когда появится)
  useEffect(() => {
    if (!activeReplyKeyboard?.keyboard) return;
    setIsReplyKeyboardOpen(true);
  }, [activeReplyKeyboard]);

  // Скрываем bottom nav когда открыт конкретный чат
  useEffect(() => {
    if (!showUserList) {
      document.documentElement.classList.add('in-chat-conversation');
    } else {
      document.documentElement.classList.remove('in-chat-conversation');
    }
    return () => {
      document.documentElement.classList.remove('in-chat-conversation');
    };
  }, [showUserList]);

  // Скрываем bottom nav и перепозиционируем input при открытии экранной клавиатуры
  useEffect(() => {
    if (showUserList) {
      document.documentElement.classList.remove('keyboard-open');
      document.documentElement.style.removeProperty('--vvp-height');
      document.documentElement.style.removeProperty('--chat-keyboard-inset');
      return;
    }

    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // iOS can shift visualViewport with offsetTop when keyboard/address bar animates.
      // We use the effective visible height to avoid phantom extra scroll space.
      const effectiveViewportHeight = Math.round(vv.height + vv.offsetTop);
      const kbH = Math.round(Math.max(0, window.innerHeight - effectiveViewportHeight));
      const isKbOpen = kbH > 100;
      const wasKbOpen = document.documentElement.classList.contains('keyboard-open');
      document.documentElement.classList.toggle('keyboard-open', isKbOpen);
      document.documentElement.style.setProperty('--vvp-height', `${effectiveViewportHeight}px`);
      document.documentElement.style.setProperty('--chat-keyboard-inset', `${kbH}px`);
      // Scroll to bottom when keyboard opens (Telegram-style)
      if (isKbOpen && !wasKbOpen) {
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 80);
      }
    };

    update(); // задаём высоту сразу при открытии чата
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.classList.remove('keyboard-open');
      document.documentElement.style.removeProperty('--vvp-height');
      document.documentElement.style.removeProperty('--chat-keyboard-inset');
    };
  }, [showUserList]);

  // Используем useRef для хранения актуальных значений
  const selectedUserRef = useRef(selectedUser);
  const selectedGroupRef = useRef(selectedGroup);
  const selectedChannelRef = useRef(selectedChannel);
  const currentUserRef = useRef(currentUser);
  const addMessageRef = useRef(addMessage);
  const messageDeletedHandlerRef = useRef(null);
  const dialogDeletedHandlerRef = useRef(null);
  const receivedMessageIdsRef = useRef(new Set());

  // Обновляем refs при изменении значений
  useEffect(() => {
    selectedUserRef.current = selectedUser;
    selectedGroupRef.current = selectedGroup;
    selectedChannelRef.current = selectedChannel;
    currentUserRef.current = currentUser;
    addMessageRef.current = addMessage;
  }, [selectedUser, selectedGroup, selectedChannel, currentUser, addMessage]);

  // Создаем стабильную функцию-обработчик, которая использует refs
  const handleNewMessage = React.useCallback((data) => {
    if (data.message) {
      const message = data.message;
      const messageId = message.id || message.messageId;

      // Дедупликация: игнорируем дубликаты при реконнекте
      if (messageId && receivedMessageIdsRef.current.has(messageId)) {
        console.log('⚠️ [Chat] Duplicate message ignored:', messageId);
        return;
      }
      if (messageId) {
        receivedMessageIdsRef.current.add(messageId);
        // Держим только последние 1000 ID
        if (receivedMessageIdsRef.current.size > 1000) {
          const ids = Array.from(receivedMessageIdsRef.current);
          receivedMessageIdsRef.current = new Set(ids.slice(-500));
        }
      }

      const currentUserId = currentUserRef.current?.id;
      const selectedUserId = selectedUserRef.current?.id;

      const isForCurrentChat =
        (selectedUserId != null && (String(message.senderId) === String(selectedUserId) || String(message.receiverId) === String(selectedUserId))) ||
        (selectedGroupRef.current && String(message.groupId) === String(selectedGroupRef.current.id)) ||
        (selectedChannelRef.current && String(message.channelId) === String(selectedChannelRef.current.id));

      // Добавляем сообщение если:
      // - оно принадлежит текущему диалогу
      // - И (отправитель не мы ИЛИ это переслан. сообщение от нас — forwardedFromId есть)
      const isMyForward = !!message.forwardedFromId && String(message.senderId) === String(currentUserId);
      if (isForCurrentChat && (String(message.senderId) !== String(currentUserId) || isMyForward)) {
        addMessageRef.current(message);
      }
      // Список диалогов обновляется инкрементально в useChatStore (без refetch API)
    }
  }, []);

  // Используем useRef для хранения флага инициализации
  const isInitializedRef = useRef(false);
  const handleNewMessageRef = useRef(handleNewMessage);

  // Обновляем ref для handleNewMessage
  useEffect(() => {
    handleNewMessageRef.current = handleNewMessage;
  }, [handleNewMessage]);

  const loadBotCommands = React.useCallback(async (botUserId) => {
    try {
      const commands = await chatApi.getBotCommands(botUserId);
      setBotCommands(commands || []);
    } catch (error) {
      console.error('Failed to load bot commands:', error);
      setBotCommands([]);
    }
  }, []);

  const initializeChat = React.useCallback(async () => {
    if (isInitializedRef.current) {
      console.log('⚠️ [Chat] Already initialized, skipping');
      return;
    }

    isInitializedRef.current = true;

    try {
      setError(null);
      console.log('🔧 [Chat] Initializing chat UI...');

      setCurrentUser({
        id: userId,
        username: user.username,
        displayName: user.fullName || user.username,
        avatar: user.avatar ?? null,
      });

      // Свежие диалоги при каждом входе на страницу (кэш/store уже показан мгновенно)
      revalidateOnChatOpen();

      chatWebsocket.on('new_message', handleNewMessageRef.current);
      
      // Обработчики для реакций
      chatWebsocket.on('reaction_added', (data) => {
        if (data.messageId && data.reaction) {
          setMessageReactions(prev => {
            const current = prev[data.messageId] || {};
            const emojiReactions = current[data.reaction.emoji] || [];
            return {
              ...prev,
              [data.messageId]: {
                ...current,
                [data.reaction.emoji]: [...emojiReactions, data.reaction]
              }
            };
          });
        }
      });

      chatWebsocket.on('reaction_removed', (data) => {
        if (data.messageId && data.userId && data.emoji) {
          setMessageReactions(prev => {
            const current = prev[data.messageId] || {};
            const emojiReactions = current[data.emoji] || [];
            return {
              ...prev,
              [data.messageId]: {
                ...current,
                [data.emoji]: emojiReactions.filter(r => r.userId !== data.userId)
              }
            };
          });
        }
      });

      const onMessageDeleted = (data) => {
        if (data.messageId) {
          const isCurrentChat = (data.chatType === 'user' && selectedUserRef.current && (selectedUserRef.current.id === data.userId || String(selectedUserRef.current.id) === String(data.userId)))
            || (data.chatType === 'group' && selectedGroupRef.current && selectedGroupRef.current.id === data.groupId)
            || (data.chatType === 'channel' && selectedChannelRef.current && selectedChannelRef.current.id === data.channelId);
          if (isCurrentChat) {
            setMessages(prev => prev.filter(m => String(m.id) !== String(data.messageId)));
          }
        }
      };
      const onDialogDeleted = () => {
        fetchDialogs({ silent: true });
      };
      const onMessageEdited = (data) => {
        if (data.messageId && data.content !== undefined) {
          setMessages(prev => prev.map(m =>
            String(m.id) === String(data.messageId)
              ? { ...m, text: data.content, editedAt: data.editedAt }
              : m
          ));
        }
      };
      chatWebsocket.on('message_edited', onMessageEdited);

      messageDeletedHandlerRef.current = onMessageDeleted;
      dialogDeletedHandlerRef.current = onDialogDeleted;
      chatWebsocket.on('message_deleted', onMessageDeleted);
      chatWebsocket.on('dialog_deleted', onDialogDeleted);
      
      console.log('✅ [Chat] Initialization complete');
    } catch (error) {
      console.error('❌ [Chat] Failed to initialize chat:', error);
      setError(error.message || 'Ошибка при инициализации чата');
      isInitializedRef.current = false;
    }
  }, [token, user, userId, revalidateOnChatOpen, fetchDialogs]);

  // Контакты и поиск — грузим пользователей только по требованию
  useEffect(() => {
    const needUsers =
      (viewMode === 'contacts' || searchQuery.trim().length > 0) &&
      users.length === 0 &&
      !usersLoading;
    if (needUsers) {
      fetchUsers();
    }
  }, [viewMode, searchQuery, users.length, usersLoading, fetchUsers]);

  useEffect(() => {
    console.log('🔍 [Chat] useEffect triggered', { token: !!token, user: !!user, isInitialized: isInitializedRef.current });
    
    if (token && user && !isInitializedRef.current) {
      console.log('🚀 [Chat] Calling initializeChat');
      initializeChat();
    } else if (!token || !user) {
      console.log('⚠️ [Chat] Missing token or user, resetting initialization');
      isInitializedRef.current = false;
    }

    return () => {
      console.log('🧹 [Chat] Cleanup — removing own listeners only, keeping WS alive');
      if (isInitializedRef.current) {
        chatWebsocket.off('new_message', handleNewMessageRef.current);
        chatWebsocket.off('reaction_added');
        chatWebsocket.off('reaction_removed');
        if (messageDeletedHandlerRef.current) {
          chatWebsocket.off('message_deleted', messageDeletedHandlerRef.current);
        }
        if (dialogDeletedHandlerRef.current) {
          chatWebsocket.off('dialog_deleted', dialogDeletedHandlerRef.current);
        }
        // НЕ вызываем chatWebsocket.disconnect() — глобальные listeners в useChatStore.
        setActiveDialogKey(null);
        isInitializedRef.current = false;
      }
    };
  }, [token, user, initializeChat, setActiveDialogKey]);

  // Объявляем функции загрузки сообщений до их использования в handleSelectGroup/handleSelectChannel
  const loadGroupMessages = React.useCallback(async (groupId) => {
    try {
      const messageList = await chatApi.getGroupMessages(groupId);
      const formattedMessages = messageList.map(msg => {
        const normalizedMsg = normalizeMessage(msg);
        return {
          id: normalizedMsg.id,
          text: normalizedMsg.content,
          sender: normalizedMsg.sender_id === currentUser?.id ? 'outgoing' : 'incoming',
          senderName: normalizedMsg.sender?.display_name || normalizedMsg.sender?.displayName || normalizedMsg.sender?.full_name || normalizedMsg.sender?.username,
          senderAvatar: normalizedMsg.sender?.avatar,
          time: new Date(normalizedMsg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          date: formatDate(new Date(normalizedMsg.created_at)),
          messageType: normalizedMsg.message_type,
          image: normalizedMsg.image_data,
          isRead: normalizedMsg.is_read,
          forwardedFromId: normalizedMsg.forwarded_from_id,
          originalSender: normalizedMsg.original_sender,
          inReplyToId: normalizedMsg.in_reply_to_id,
          replyTo: normalizedMsg.reply_to,
          isPinned: normalizedMsg.is_pinned
        };
      });
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Failed to load group messages:', error);
    }
  }, [currentUser]);

  const loadChannelMessages = React.useCallback(async (channelId) => {
    try {
      const messageList = await chatApi.getChannelMessages(channelId);
      const formattedMessages = messageList.map(msg => {
        const normalizedMsg = normalizeMessage(msg);
        return {
          id: normalizedMsg.id,
          text: normalizedMsg.content,
          sender: normalizedMsg.sender_id === currentUser?.id ? 'outgoing' : 'incoming',
          senderName: normalizedMsg.sender?.display_name || normalizedMsg.sender?.displayName || normalizedMsg.sender?.full_name || normalizedMsg.sender?.username,
          senderAvatar: normalizedMsg.sender?.avatar,
          time: new Date(normalizedMsg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          date: formatDate(new Date(normalizedMsg.created_at)),
          messageType: normalizedMsg.message_type,
          image: normalizedMsg.image_data,
          isRead: normalizedMsg.is_read,
          forwardedFromId: normalizedMsg.forwarded_from_id,
          originalSender: normalizedMsg.original_sender,
          inReplyToId: normalizedMsg.in_reply_to_id,
          replyTo: normalizedMsg.reply_to,
          isPinned: normalizedMsg.is_pinned,
          replyMarkup: normalizedMsg.reply_markup
        };
      });
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Failed to load channel messages:', error);
    }
  }, [currentUser]);

  // Объявляем функции выбора канала и группы до их использования в useEffect
  // Оптимистично обнуляем счётчик непрочитанных в списке диалогов
  const markDialogRead = React.useCallback((type, id) => {
    markDialogReadInStore(type, id);
  }, [markDialogReadInStore]);

  const handleSelectGroup = React.useCallback(async (group) => {
    setMessages([]);
    setSelectedGroup(group);
    setSelectedUser(null);
    setSelectedChannel(null);
    setReplyingToMessage(null);
    setShowUserList(false);
    setShowCreateChannelModal(false);
    setShowCreateGroupModal(false);
    setShowChannelManagementModal(false);
    setShowGroupManagementModal(false);
    setActiveDialogKey(dialogKey({ type: 'group', id: group.id }));
    markDialogRead('group', group.id);
    if (window.location.pathname.startsWith('/chat')) {
      navigate(`/chat/group/${group.id}`, { replace: true });
    }
    await loadGroupMessages(group.id);
    chatApi.markMessagesAsRead({ groupId: group.id }).catch(() => {});
  }, [navigate, loadGroupMessages, markDialogRead, setActiveDialogKey]);

  const handleSelectChannel = React.useCallback(async (channel) => {
    setMessages([]);
    setSelectedChannel(channel);
    setSelectedUser(null);
    setSelectedGroup(null);
    setReplyingToMessage(null);
    setShowUserList(false);
    setShowCreateChannelModal(false);
    setShowCreateGroupModal(false);
    setShowChannelManagementModal(false);
    setShowGroupManagementModal(false);
    setActiveDialogKey(dialogKey({ type: 'channel', id: channel.id }));
    markDialogRead('channel', channel.id);
    if (window.location.pathname.startsWith('/chat')) {
      navigate(`/chat/channel/${channel.id}`, { replace: true });
    }
    await loadChannelMessages(channel.id);
    chatApi.markMessagesAsRead({ channelId: channel.id }).catch(() => {});
  }, [navigate, loadChannelMessages, markDialogRead, setActiveDialogKey]);

  // Обработка URL параметров для открытия канала или группы
  useEffect(() => {
    if (!isInitializedRef.current || loading) return;

    const loadFromUrl = async () => {
      if (channelId && !selectedChannel) {
        try {
          // Пробуем найти в загруженных каналах
          let channel = channels.find(c => c.id === parseInt(channelId));
          
          // Если не нашли, загружаем детали канала
          if (!channel) {
            try {
              const details = await chatApi.getChannelDetails(channelId);
              channel = {
                id: details.id,
                name: details.name,
                description: details.description,
                avatar: details.avatar
              };
            } catch (err) {
              // Если не удалось загрузить, возможно пользователь не подписан
              console.error('Failed to load channel details:', err);
              // Пробуем подписаться автоматически
              try {
                await chatApi.subscribeToChannel(channelId);
                const details = await chatApi.getChannelDetails(channelId);
                channel = {
                  id: details.id,
                  name: details.name,
                  description: details.description,
                  avatar: details.avatar
                };
                // Обновляем список каналов
                fetchChannels({ silent: true });
              } catch (subscribeErr) {
                console.error('Failed to subscribe to channel:', subscribeErr);
                return;
              }
            }
          }
          
          if (channel) {
            await handleSelectChannel(channel);
          }
        } catch (err) {
          console.error('Failed to load channel from URL:', err);
        }
      } else if (groupId && !selectedGroup) {
        try {
          // Пробуем найти в загруженных группах
          let group = groups.find(g => g.id === parseInt(groupId));
          
          // Если не нашли, загружаем детали группы
          if (!group) {
            try {
              const details = await chatApi.getGroupDetails(groupId);
              group = {
                id: details.id,
                name: details.name,
                description: details.description,
                avatar: details.avatar
              };
            } catch (err) {
              console.error('Failed to load group details:', err);
              // Если не удалось загрузить, возможно пользователь не является участником
              return;
            }
          }
          
          if (group) {
            await handleSelectGroup(group);
          }
        } catch (err) {
          console.error('Failed to load group from URL:', err);
        }
      } else if (!channelId && !groupId && (selectedChannel || selectedGroup)) {
        // Если параметров нет, но есть выбранный чат, возвращаемся к списку
        setShowUserList(true);
        setSelectedChannel(null);
        setSelectedGroup(null);
        setSelectedUser(null);
        setMessages([]);
      }
    };

    loadFromUrl();
  }, [channelId, groupId, loading, channels, groups, selectedChannel, selectedGroup, handleSelectChannel, handleSelectGroup, fetchChannels]);

  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = async (event) => {
      const updatedProfile = event.detail;
      console.log('🔄 [Chat] Profile updated, refreshing currentUser:', updatedProfile);

      // Обновляем локальный currentUser в чате
      if (updatedProfile) {
        setCurrentUser(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            username: updatedProfile.username !== undefined ? updatedProfile.username : prev.username,
            displayName: updatedProfile.displayName !== undefined ? updatedProfile.displayName : prev.displayName,
            fullName: updatedProfile.fullName !== undefined ? updatedProfile.fullName : prev.fullName,
            avatar: updatedProfile.avatar !== undefined ? updatedProfile.avatar : prev.avatar
          };
        });

        // Перезагружаем диалоги и пользователей для обновления аватаров
        fetchDialogs({ silent: true });
        fetchUsers({ force: true });
      } else {
        // detail === null means data was already updated via verify() in Settings
        // Just reload dialogs and users to ensure avatars are updated
        fetchDialogs({ silent: true });
        fetchUsers({ force: true });
      }
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [user, fetchDialogs, fetchUsers, updateUser]);

  const loadMessages = async (userId) => {
    try {
      const messageList = await chatApi.getMessages(userId);
      const formattedMessages = messageList.map(msg => {
        const normalizedMsg = normalizeMessage(msg);
        return {
          id: normalizedMsg.id,
          text: normalizedMsg.content,
          sender: normalizedMsg.sender_id === currentUser?.id ? 'outgoing' : 'incoming',
          senderName: normalizedMsg.sender?.display_name || normalizedMsg.sender?.displayName || normalizedMsg.sender?.full_name || normalizedMsg.sender?.username,
          senderAvatar: normalizedMsg.sender?.avatar,
          time: new Date(normalizedMsg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          date: formatDate(new Date(normalizedMsg.created_at)),
          messageType: normalizedMsg.message_type,
          image: normalizedMsg.image_data,
          audio: normalizedMsg.audio_data,
          isRead: normalizedMsg.is_read,
          forwardedFromId: normalizedMsg.forwarded_from_id,
          originalSender: normalizedMsg.original_sender,
          inReplyToId: normalizedMsg.in_reply_to_id,
          replyTo: normalizedMsg.reply_to,
          isPinned: normalizedMsg.is_pinned,
          replyMarkup: normalizedMsg.reply_markup
        };
      });
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };


  const getActiveDialogEntity = () => {
    if (selectedGroup) return { type: 'group', id: selectedGroup.id };
    if (selectedChannel) return { type: 'channel', id: selectedChannel.id };
    if (selectedUser) {
      return selectedUser.isSaved
        ? { type: 'saved', id: selectedUser.id }
        : { type: 'user', id: selectedUser.id };
    }
    return null;
  };

  const handleSelectUser = async (user) => {
    setMessages([]);
    setSelectedUser(user);
    setSelectedGroup(null);
    setSelectedChannel(null);
    setReplyingToMessage(null);
    setShowUserList(false);
    setShowCreateChannelModal(false);
    setShowCreateGroupModal(false);
    setShowChannelManagementModal(false);
    setShowGroupManagementModal(false);
    setActiveDialogKey(
      user.isSaved
        ? dialogKey({ type: 'saved', id: user.id })
        : dialogKey({ type: 'user', id: user.id })
    );
    markDialogRead(user.isSaved ? 'saved' : 'user', user.id);
    navigate('/chat', { replace: true });
    await loadMessages(user.id);
    chatApi.markMessagesAsRead({ senderId: user.id }).catch(() => {});
    if (user?.isBot) {
      await loadBotCommands(user.id);
    } else {
      setBotCommands([]);
    }
  };

  const handleSelectDialog = async (dialog) => {
    if (dialog.type === 'saved') {
      await handleSelectUser({
        id: dialog.id,
        username: dialog.username,
        displayName: 'Избранное',
        fullName: 'Избранное',
        avatar: dialog.avatar,
        status: null,
        isBot: false,
        isSaved: true
      });
      return;
    }
    if (dialog.type === 'user') {
      const user = users.find(u => u.id === dialog.id) || {
        id: dialog.id,
        username: dialog.username,
        displayName: dialog.displayName,
        fullName: dialog.fullName,
        avatar: dialog.avatar,
        status: dialog.status,
        isBot: dialog.isBot
      };
      await handleSelectUser(user);
    } else if (dialog.type === 'group') {
      const group = groups.find(g => g.id === dialog.id) || {
        id: dialog.id,
        name: dialog.name,
        avatar: dialog.avatar
      };
      await handleSelectGroup(group);
    } else if (dialog.type === 'channel') {
      const channel = channels.find(c => c.id === dialog.id) || {
        id: dialog.id,
        name: dialog.name,
        avatar: dialog.avatar
      };
      await handleSelectChannel(channel);
    }
  };

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;
    if (!selectedUser && !selectedGroup && !selectedChannel) return;

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      text: text,
      sender: 'outgoing',
      senderName: currentUser.displayName || currentUser.username,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: formatDate(new Date()),
      messageType: 'text',
      inReplyToId: replyingToMessage?.id || null,
      replyTo: replyingToMessage || null
    };

    setMessages(prev => [...prev, tempMessage]);
    setReplyingToMessage(null);
    const activeDialog = getActiveDialogEntity();
    if (activeDialog) {
      bumpDialogOnOutgoing(activeDialog, { text, messageType: 'text' });
    }

    try {
      let response;
      if (selectedGroup) {
        response = await chatApi.sendGroupMessage(selectedGroup.id, text, 'text', null, replyingToMessage?.id || null);
      } else if (selectedChannel) {
        response = await chatApi.sendChannelMessage(selectedChannel.id, text, 'text', null, null);
      } else {
        response = await chatApi.sendMessage(selectedUser.id, text, 'text', null, replyingToMessage?.id || null);
      }

      setMessages(prev => prev.map(msg =>
        msg.id === tempId ? {
          ...tempMessage,
          id: response.id,
          createdAt: response.createdAt,
          inReplyToId: response.inReplyToId,
          replyTo: response.replyTo
        } : msg
      ));

      chatWebsocket.sendMessage(
        selectedUser?.id || null,
        text,
        response.id,
        response.createdAt,
        {
          displayName: currentUser.displayName,
          username: currentUser.username,
          avatar: currentUser.avatar
        },
        'text',
        null,
        selectedGroup?.id || null,
        response.inReplyToId,
        null,
        selectedChannel?.id || null
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
    }
  };

  const handleSendImage = async (imageFile, caption) => {
    if (!selectedUser && !selectedGroup && !selectedChannel) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const imageData = e.target.result;
      const tempId = `temp-${Date.now()}`;
      const tempMessage = {
        id: tempId,
        text: caption || null,
        sender: 'outgoing',
        senderName: currentUser.displayName || currentUser.username,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        date: formatDate(new Date()),
        messageType: 'image',
        image: imageData,
        inReplyToId: replyingToMessage?.id || null,
        replyTo: replyingToMessage || null
      };

      setMessages(prev => [...prev, tempMessage]);
      setReplyingToMessage(null);
      const activeDialog = getActiveDialogEntity();
      if (activeDialog) {
        bumpDialogOnOutgoing(activeDialog, { text: caption, messageType: 'image' });
      }

      try {
        let response;
        if (selectedGroup) {
          response = await chatApi.sendGroupMessage(selectedGroup.id, caption || null, 'image', imageData, replyingToMessage?.id || null);
        } else if (selectedChannel) {
          response = await chatApi.sendChannelMessage(selectedChannel.id, caption || null, 'image', imageData, null);
        } else {
          response = await chatApi.sendMessage(selectedUser.id, caption || null, 'image', imageData, replyingToMessage?.id || null);
        }

        setMessages(prev => prev.map(msg =>
          msg.id === tempId ? {
            ...tempMessage,
            id: response.id,
            createdAt: response.createdAt
          } : msg
        ));

        chatWebsocket.sendMessage(
          selectedUser?.id || null,
          caption || '',
          response.id,
          response.createdAt,
          {
            displayName: currentUser.displayName,
            username: currentUser.username,
            avatar: currentUser.avatar
          },
          'image',
          imageData,
          selectedGroup?.id || null,
          response.inReplyToId,
          null,
          selectedChannel?.id || null
        );
      } catch (error) {
        console.error('Failed to send image:', error);
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
      }
    };
    reader.readAsDataURL(imageFile);
  };

  const handleSendSticker = async (filename) => {
    if (!selectedUser && !selectedGroup && !selectedChannel) return;

    const stickerUrl = chatApi.getStickerUrl(filename);
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      text: null,
      sender: 'outgoing',
      senderName: currentUser.displayName || currentUser.username,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: formatDate(new Date()),
      messageType: 'sticker',
      image: stickerUrl,
    };

    setMessages(prev => [...prev, tempMessage]);
    const activeDialog = getActiveDialogEntity();
    if (activeDialog) {
      bumpDialogOnOutgoing(activeDialog, { text: '🎨 Стикер', messageType: 'sticker' });
    }

    try {
      let response;
      if (selectedGroup) {
        response = await chatApi.sendGroupMessage(selectedGroup.id, null, 'sticker', filename, null);
      } else if (selectedChannel) {
        response = await chatApi.sendChannelMessage(selectedChannel.id, null, 'sticker', filename, null);
      } else {
        response = await chatApi.sendMessage(selectedUser.id, null, 'sticker', filename, null);
      }

      setMessages(prev => prev.map(msg =>
        msg.id === tempId ? { ...tempMessage, id: response.id } : msg
      ));

      chatWebsocket.sendMessage(
        selectedUser?.id || null,
        null,
        response.id,
        response.createdAt,
        {
          displayName: currentUser.displayName,
          username: currentUser.username,
          avatar: currentUser.avatar
        },
        'sticker',
        filename,
        selectedGroup?.id || null,
        null,
        null,
        selectedChannel?.id || null
      );
    } catch (error) {
      console.error('Failed to send sticker:', error);
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
    }
  };

  const handleSendVoice = async (audioData) => {
    if (!selectedUser && !selectedGroup && !selectedChannel) return;

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      text: null,
      sender: 'outgoing',
      senderName: currentUser.displayName || currentUser.username,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: formatDate(new Date()),
      messageType: 'audio',
      audio: audioData,
      inReplyToId: replyingToMessage?.id || null,
      replyTo: replyingToMessage || null
    };

    setMessages(prev => [...prev, tempMessage]);
    setReplyingToMessage(null);
    const activeDialog = getActiveDialogEntity();
    if (activeDialog) {
      bumpDialogOnOutgoing(activeDialog, { messageType: 'audio' });
    }

    try {
      let response;
      if (selectedGroup) {
        response = await chatApi.sendGroupMessage(selectedGroup.id, null, 'audio', audioData, replyingToMessage?.id || null);
      } else if (selectedChannel) {
        response = await chatApi.sendChannelMessage(selectedChannel.id, null, 'audio', null, audioData);
      } else {
        response = await chatApi.sendMessage(selectedUser.id, null, 'audio', audioData, replyingToMessage?.id || null);
      }

      setMessages(prev => prev.map(msg =>
        msg.id === tempId ? {
          ...tempMessage,
          id: response.id,
          createdAt: response.createdAt
        } : msg
      ));

      chatWebsocket.sendMessage(
        selectedUser?.id || null,
        '',
        response.id,
        response.createdAt,
        {
          displayName: currentUser.displayName,
          username: currentUser.username,
          avatar: currentUser.avatar
        },
        'audio',
        null,
        selectedGroup?.id || null,
        response.inReplyToId,
        audioData,
        selectedChannel?.id || null
      );
    } catch (error) {
      console.error('Failed to send voice message:', error);
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
    }
  };

  const handleReply = (message) => {
    setReplyingToMessage(message);
  };

  const handleCancelReply = () => {
    setReplyingToMessage(null);
  };

  // Загрузить реакции для сообщения
  const loadMessageReactions = React.useCallback(async (messageId) => {
    try {
      // Для временных сообщений (optimistic UI) пропускаем загрузку реакций
      const idStr = String(messageId ?? '');
      if (!/^\d+$/.test(idStr)) return;
      const reactions = await chatApi.getMessageReactions(messageId);
      setMessageReactions(prev => ({
        ...prev,
        [messageId]: reactions
      }));
    } catch (error) {
      console.error('Failed to load message reactions:', error);
    }
  }, []);

  // Загрузить реакции для всех сообщений
  const loadAllReactions = React.useCallback(async () => {
    if (messages.length === 0) return;
    const messageIds = messages
      .map(m => m.id)
      .filter(id => /^\d+$/.test(String(id ?? '')));
    // Загружаем реакции для всех сообщений параллельно
    await Promise.all(messageIds.map(id => loadMessageReactions(id)));
  }, [messages, loadMessageReactions]);

  // Загрузить реакции при загрузке сообщений
  const messagesIdsRef = useRef([]);
  useEffect(() => {
    const currentIds = messages.map(m => m.id).sort().join(',');
    if (currentIds !== messagesIdsRef.current && messages.length > 0 && !loading) {
      messagesIdsRef.current = currentIds;
      loadAllReactions();
    }
  }, [messages, loading, loadAllReactions]);

  // Добавить реакцию
  const handleAddReaction = async (messageId, emoji) => {
    try {
      await chatApi.addReaction(messageId, emoji);
      // Обновим реакции локально
      await loadMessageReactions(messageId);
      setShowReactionPicker(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  // Удалить реакцию
  const handleRemoveReaction = async (messageId, emoji) => {
    try {
      await chatApi.removeReaction(messageId, emoji);
      // Обновим реакции локально
      await loadMessageReactions(messageId);
    } catch (error) {
      console.error('Failed to remove reaction:', error);
    }
  };

  // Переключить реакцию (добавить или удалить)
  const handleToggleReaction = async (messageId, emoji) => {
    const reactions = messageReactions[messageId] || {};
    const emojiReactions = reactions[emoji] || [];
    const hasReaction = emojiReactions.some(r => r.userId === currentUser?.id);

    if (hasReaction) {
      await handleRemoveReaction(messageId, emoji);
    } else {
      await handleAddReaction(messageId, emoji);
    }
  };

  // ── Контекстное меню ──────────────────────────────────────────────────────

  const openContextMenuAt = (clientX, clientY, message) => {
    const menuW = 230;
    const menuH = 290;
    const x = clientX + menuW > window.innerWidth ? window.innerWidth - menuW - 8 : clientX;
    const y = clientY + menuH > window.innerHeight ? clientY - menuH : clientY;
    setContextMenu({ x: Math.max(8, x), y: Math.max(8, y), message });
    setContextMenuReactionOpen(false);
  };

  const closeContextMenu = () => {
    setContextMenu(null);
    setContextMenuReactionOpen(false);
  };

  const handleMsgContextMenu = (e, message) => {
    e.preventDefault();
    if (selectionMode) { handleSelectMessage(message.id); return; }
    openContextMenuAt(e.clientX, e.clientY, message);
  };

  // ── Выделение сообщений ────────────────────────────────────────────────────

  const handleSelectMessage = (id) => {
    setSelectedMessages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedMessages(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.size === 0) return;
    const ok = await confirm({
      title: 'Удалить сообщения',
      message: `Удалить ${selectedMessages.size} сообщ.?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    for (const id of selectedMessages) {
      try {
        await chatApi.deleteMessage(id);
        setMessages(prev => prev.filter(m => String(m.id) !== String(id)));
      } catch (err) {
        console.error('Bulk delete error:', err);
      }
    }
    exitSelectionMode();
  };

  const handleBulkForward = () => {
    if (selectedMessages.size === 0) return;
    const msgs = messages.filter(m => selectedMessages.has(m.id));
    setForwardingMessage({ bulk: true, messages: msgs });
    setShowForwardModal(true);
    exitSelectionMode();
  };

  // ── Долгое зажатие: общая отмена ──────────────────────────────────────────

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // ── Touch-обработчики (мобильные) ─────────────────────────────────────────
  // Долгий тап → контекстное меню (НЕ режим выделения)
  // Короткий тап → пропускаем (onClick обработает selectionMode)

  const handleTouchStart = (e, message) => {
    const touch = e.touches[0];
    touchPosRef.current = { x: touch.clientX, y: touch.clientY };
    longPressActivatedRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressActivatedRef.current = true;
      longPressTimerRef.current = null;
      if (navigator.vibrate) navigator.vibrate(50);
      openContextMenuAt(touchPosRef.current.x, touchPosRef.current.y, message);
    }, 500);
  };

  const handleTouchEnd = (e, message) => {
    if (longPressActivatedRef.current) {
      // Долгий тап только что сработал — глушим следующий click
      e.preventDefault();
      longPressActivatedRef.current = false;
      return;
    }
    cancelLongPress();
    // Короткий тап: если в режиме выделения — переключаем сообщение
    // (без e.preventDefault — пусть onClick тоже обработает)
  };

  const handleTouchCancel = () => cancelLongPress();

  // ── Mouse-обработчики (долгое зажатие ЛКМ на десктопе → режим выделения) ──

  const startLongPress = (message) => {
    longPressActivatedRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressActivatedRef.current = true;
      longPressTimerRef.current = null;
      if (navigator.vibrate) navigator.vibrate(50);
      setSelectionMode(true);
      setSelectedMessages(new Set([message.id]));
    }, 500);
  };

  const handleMouseDown = (e, message) => {
    if (e.button !== 0) return; // только ЛКМ
    startLongPress(message);
  };

  const handleMouseUp = (e) => {
    if (e.button !== 0) return;
    cancelLongPress();
    // longPressActivatedRef сбросится в onClick
  };

  // ── Прокрутка к оригинальному сообщению (для пересланных) ───────────────

  const scrollToMessage = (messageId) => {
    // Ищем только среди сообщений текущего диалога
    const exists = messages.some(m => String(m.id) === String(messageId));
    if (!exists) return; // оригинал в другом чате — ничего не делаем
    const el = document.querySelector(`[data-message-id="${messageId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('message-highlight');
    setTimeout(() => el.classList.remove('message-highlight'), 1800);
  };

  // Редактировать сообщение (только своё текстовое)
  const handleStartEdit = (message) => {
    setEditingMessage({ id: message.id, text: message.text || '' });
    setReplyingToMessage(null);
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.value = message.text || '';
        messageInputRef.current.focus();
        const len = messageInputRef.current.value.length;
        messageInputRef.current.setSelectionRange(len, len);
      }
    }, 0);
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    if (messageInputRef.current) messageInputRef.current.value = '';
  };

  const handleSaveEdit = async (newText) => {
    if (!editingMessage || !newText.trim()) return;
    const { id, text: oldText } = editingMessage;
    if (newText.trim() === oldText.trim()) {
      handleCancelEdit();
      return;
    }
    // Оптимистичное обновление
    setMessages(prev => prev.map(m =>
      String(m.id) === String(id)
        ? { ...m, text: newText.trim(), editedAt: new Date().toISOString() }
        : m
    ));
    setEditingMessage(null);
    if (messageInputRef.current) messageInputRef.current.value = '';
    try {
      await chatApi.editMessage(id, newText.trim());
    } catch (err) {
      console.error('Failed to edit message:', err);
      // Откатываем при ошибке
      setMessages(prev => prev.map(m =>
        String(m.id) === String(id) ? { ...m, text: oldText } : m
      ));
      showToast(err?.message || 'Не удалось изменить сообщение', { type: 'error' });
    }
  };

  // Удалить сообщение (только своё)
  const handleDeleteMessage = async (messageId) => {
    const ok = await confirm({
      title: 'Удалить сообщение',
      message: 'Удалить это сообщение?',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    try {
      await chatApi.deleteMessage(messageId);
      setMessages(prev => prev.filter(m => String(m.id) !== String(messageId)));
    } catch (err) {
      console.error('Failed to delete message:', err);
      showToast(err?.message || 'Не удалось удалить сообщение', { type: 'error', variant: 'delete' });
    }
  };

  // Удалить диалог целиком (только личный чат), вызывается из шапки чата
  const handleDeleteDialog = async () => {
    if (!selectedUser) return;
    const ok = await confirm({
      title: 'Удалить диалог',
      message: `Удалить весь диалог с ${selectedUser.displayName || selectedUser.username}? Все сообщения будут удалены без возможности восстановления.`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    try {
      await chatApi.deleteDialog(selectedUser.id);
      removeDialogFromStore('user', selectedUser.id);
      setActiveDialogKey(null);
      setSelectedUser(null);
      setMessages([]);
      setShowUserList(true);
      navigate('/chat', { replace: true });
    } catch (err) {
      console.error('Failed to delete dialog:', err);
      showToast(err?.message || 'Не удалось удалить диалог', { type: 'error', variant: 'delete' });
    }
  };

  // Удалить диалог из списка (по userId, без открытого чата)
  const handleDeleteDialogFromList = async (e, userId, displayName) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Удалить диалог',
      message: `Удалить весь диалог с ${displayName || 'пользователем'}? Все сообщения будут удалены без возможности восстановления.`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });
    if (!ok) return;
    try {
      await chatApi.deleteDialog(userId);
      removeDialogFromStore('user', userId);
      if (selectedUser?.id === userId) {
        setActiveDialogKey(null);
        setSelectedUser(null);
        setMessages([]);
        setShowUserList(true);
        navigate('/chat', { replace: true });
      }
    } catch (err) {
      console.error('Failed to delete dialog:', err);
      showToast(err?.message || 'Не удалось удалить диалог', { type: 'error', variant: 'delete' });
    }
  };

  // Переслать сообщение
  const handleForwardMessage = (message) => {
    setForwardingMessage(message);
    setShowForwardModal(true);
  };

  // Подтвердить пересылку
  const handleConfirmForward = async (selectedReceiverIds = [], selectedGroupIds = []) => {
    if (!forwardingMessage) return;
    try {
      const msgs = forwardingMessage.bulk ? forwardingMessage.messages : [forwardingMessage];
      for (const msg of msgs) {
        await chatApi.forwardMessage(msg.id, selectedReceiverIds, selectedGroupIds);
        // Сервер теперь сам рассылает WS-уведомление и отправителю, и получателю.
        // handleNewMessage обработает входящее событие и добавит сообщение в стейт.
      }
      setShowForwardModal(false);
      setForwardingMessage(null);
    } catch (error) {
      console.error('Failed to forward message:', error);
    }
  };

  const handleBackToUserList = async () => {
    setShowUserList(true);
    setSelectedUser(null);
    setSelectedGroup(null);
    setSelectedChannel(null);
    setMessages([]);
    setReplyingToMessage(null);
    setBotCommands([]);
    setShowCreateChannelModal(false);
    setShowCreateGroupModal(false);
    setShowChannelManagementModal(false);
    setShowGroupManagementModal(false);
    // Обновляем URL
    navigate('/chat', { replace: true });
    setActiveDialogKey(null);
    revalidateOnChatOpen();
  };

  /** Мгновенный переход к последнему сообщению (как Telegram/WhatsApp). */
  const scrollToBottom = React.useCallback((behavior = 'auto') => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (behavior === 'smooth') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  // Если пользователь у нижнего края — удерживаем позицию после подгрузки медиа
  const handleMessageMediaLoad = React.useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 160) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  // useLayoutEffect — до отрисовки кадра, без видимого «пролистывания» сверху вниз
  useLayoutEffect(() => {
    if (messages.length === 0) return;
    scrollToBottom('auto');
    // Дополнительный snap после layout (длинные списки, вложения)
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, scrollToBottom]);

  // Убеждаемся, что показываем чат, когда есть выбранный пользователь, группа или канал
  // Исправляем состояние, если есть активный чат, но showUserList = true
  useEffect(() => {
    const hasActiveChat = selectedUser || selectedGroup || selectedChannel;
    if (hasActiveChat && showUserList) {
      setShowUserList(false);
    }
  }, [selectedUser, selectedGroup, selectedChannel, showUserList]);

  // Фильтрация диалогов по поисковому запросу
  const filteredDialogs = dialogs.filter(dialog => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    if (dialog.type === 'user') {
      const displayName = (dialog.displayName || dialog.fullName || '').toLowerCase();
      const username = (dialog.username || '').toLowerCase();
      return displayName.includes(query) || username.includes(query);
    } else if (dialog.type === 'group' || dialog.type === 'channel') {
      const name = (dialog.name || '').toLowerCase();
      return name.includes(query);
    }
    return false;
  });

  // Фильтрация пользователей по поисковому запросу (для режима контактов)
  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const displayName = (user.displayName || user.fullName || '').toLowerCase();
    const username = (user.username || '').toLowerCase();
    
    return displayName.includes(query) || username.includes(query);
  });

  const filteredGroups = groups.filter(group => {
    const name = group.name?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query);
  });

  const filteredChannels = channels.filter(channel => {
    const name = channel.name?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    return name.includes(query);
  });

  if (!token || !user) {
    return (
      <div className="chat-loading">
        <div>Требуется авторизация</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-loading">
        <div>Ошибка: {error}</div>
        <button onClick={() => { setError(null); isInitializedRef.current = false; initializeChat(); }}>
          Попробовать снова
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="integrated-chat-screen chat-list-screen">
        <div className="chat-user-list">
          {/* Шапка */}
          <header className="chat-list-header">
            <div className="h-7 w-20 bg-slate-200 rounded-xl animate-pulse" />
            <div className="flex gap-2">
              <div className="w-8 h-8 bg-slate-200 rounded-full animate-pulse" />
              <div className="w-8 h-8 bg-slate-200 rounded-full animate-pulse" />
              <div className="w-8 h-8 bg-slate-200 rounded-full animate-pulse" />
            </div>
          </header>

          {/* Поиск */}
          <div className="chat-search">
            <div className="chat-search-field">
              <div className="w-full h-10 bg-slate-100 rounded-2xl animate-pulse" />
            </div>
          </div>

          {/* Элементы списка */}
          <div className="chat-list-body">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="chat-list-item pointer-events-none" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="chat-list-avatar">
                  <div className="w-full h-full rounded-full bg-slate-200 animate-pulse" />
                </div>
                <div className="flex flex-col gap-2 flex-1 min-w-0 py-0.5">
                  <div className="flex justify-between items-center gap-3">
                    <div className={`h-3.5 bg-slate-200 rounded-lg animate-pulse ${i % 3 === 0 ? 'w-28' : i % 3 === 1 ? 'w-36' : 'w-24'}`} style={{ animationDelay: `${i * 60}ms` }} />
                    <div className="h-2.5 w-10 bg-slate-100 rounded-lg animate-pulse" style={{ animationDelay: `${i * 60 + 30}ms` }} />
                  </div>
                  <div className={`h-2.5 bg-slate-100 rounded-lg animate-pulse ${i % 2 === 0 ? 'w-48' : 'w-40'}`} style={{ animationDelay: `${i * 60 + 20}ms` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Модальные окна (рендерятся всегда)
  const modals = (
    <>
      <CreateChannelModal
        isOpen={showCreateChannelModal}
        onClose={() => setShowCreateChannelModal(false)}
        onSuccess={async (channel) => {
          fetchChannels({ silent: true });
          fetchDialogs({ silent: true });
          setShowCreateChannelModal(false);
        }}
      />

      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onSuccess={async (group) => {
          fetchGroups({ silent: true });
          fetchDialogs({ silent: true });
          setShowCreateGroupModal(false);
        }}
      />

      {selectedChannel && (
        <ChannelManagementModal
          isOpen={showChannelManagementModal}
          onClose={() => setShowChannelManagementModal(false)}
          channel={selectedChannel}
          onUpdate={async (updatedChannel) => {
            setSelectedChannel(updatedChannel);
            fetchChannels({ silent: true });
            fetchDialogs({ silent: true });
          }}
          onDelete={async () => {
            setSelectedChannel(null);
            setShowChannelManagementModal(false);
            navigate('/chat', { replace: true });
            fetchChannels({ silent: true });
            fetchDialogs({ silent: true });
            setShowUserList(true);
          }}
          onUnsubscribe={async () => {
            setSelectedChannel(null);
            setShowChannelManagementModal(false);
            navigate('/chat', { replace: true });
            fetchChannels({ silent: true });
            fetchDialogs({ silent: true });
            setShowUserList(true);
          }}
        />
      )}

      {selectedGroup && (
        <GroupManagementModal
          isOpen={showGroupManagementModal}
          onClose={() => setShowGroupManagementModal(false)}
          group={selectedGroup}
          onUpdate={async (updatedGroup) => {
            setSelectedGroup(updatedGroup);
            fetchGroups({ silent: true });
            fetchDialogs({ silent: true });
          }}
          onDelete={async () => {
            setSelectedGroup(null);
            setShowGroupManagementModal(false);
            navigate('/chat', { replace: true });
            fetchGroups({ silent: true });
            fetchDialogs({ silent: true });
            setShowUserList(true);
          }}
          onLeave={async () => {
            setSelectedGroup(null);
            setShowGroupManagementModal(false);
            navigate('/chat', { replace: true });
            fetchGroups({ silent: true });
            fetchDialogs({ silent: true });
            setShowUserList(true);
          }}
        />
      )}
    </>
  );

  // Единый рендер с split-pane на десктопе
  const isSearchMode = searchQuery.trim().length > 0;
  const showDialogs = viewMode === 'dialogs' && !isSearchMode;
  const showContacts = viewMode === 'contacts' || isSearchMode;

  const chatName = selectedGroup
    ? selectedGroup.name
    : selectedChannel
    ? selectedChannel.name
    : selectedUser?.isSaved
    ? 'Избранное'
    : (selectedUser?.displayName || selectedUser?.username);

  const chatAvatar = selectedGroup
    ? selectedGroup.avatar
    : selectedChannel
    ? selectedChannel.avatar
    : selectedUser?.isSaved
    ? null
    : selectedUser?.avatar;

  const hasActiveChat = !!(selectedUser || selectedGroup || selectedChannel);

  return (
    <>
      {lightboxImage && (
        <PhotoLightbox
          photos={[lightboxImage]}
          initialIndex={0}
          onClose={() => setLightboxImage(null)}
        />
      )}
      <div className={`integrated-chat-screen chat-list-screen${!showUserList ? ' chat-conversation-screen' : ''} animate-in fade-in duration-300`}>

        {/* ===== ПАНЕЛЬ СПИСКА ЧАТОВ ===== */}
        <div className={`chat-split-list${!showUserList ? ' hidden md:flex' : ''} flex-col`}>
          <div className="chat-user-list">
            <header className="chat-list-header">
              <h1 className="chat-list-title">
                {isSearchMode ? 'Поиск' : viewMode === 'contacts' ? 'Контакты' : 'Чаты'}
                {dialogsRefreshing && (
                  <span className="chat-list-refreshing" title="Обновление..." aria-hidden="true" />
                )}
              </h1>
              <div className="chat-list-actions">
                {!isSearchMode && (
                  <>
                    <button
                      className="chat-header-action"
                      onClick={() => setShowCreateChannelModal(true)}
                      title="Создать канал"
                    >
                      <Megaphone size={20} />
                    </button>
                    <button
                      className="chat-header-action"
                      onClick={() => setShowCreateGroupModal(true)}
                      title="Создать группу"
                    >
                      <UsersIcon size={20} />
                    </button>
                    <button
                      className="chat-header-action chat-header-action-circle"
                      onClick={() => setViewMode(viewMode === 'dialogs' ? 'contacts' : 'dialogs')}
                      title={viewMode === 'dialogs' ? 'Показать контакты' : 'Показать диалоги'}
                    >
                      <UserIcon size={20} />
                    </button>
                  </>
                )}
              </div>
            </header>

            <div className="chat-search">
              <div className="chat-search-field">
                <Search className="chat-search-icon" size={16} />
                <input
                  type="text"
                  placeholder={viewMode === 'contacts' ? 'Поиск контактов...' : 'Поиск сообщений...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="chat-search-input"
                />
              </div>
            </div>

            <div className="chat-list-body">
              {showDialogs && (
                <>
                  {filteredDialogs.length === 0 ? (
                    <div className="empty-state">
                      <p>Нет диалогов</p>
                    </div>
                  ) : (
                    filteredDialogs.map(dialog => {
                      const lastMessagePreview = dialog.lastMessage
                        ? (dialog.lastMessageType === 'image' ? '📷 Фото'
                          : dialog.lastMessageType === 'card' ? '📌 Карточка'
                          : dialog.lastMessageType === 'audio' ? '🎵 Голосовое'
                          : dialog.lastMessageType === 'sticker' ? '🎨 Стикер'
                          : dialog.lastMessage.substring(0, 60))
                        : '';
                      const lastMessageTime = dialog.lastMessageTime 
                        ? new Date(dialog.lastMessageTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        : '';

                      return (
                        <div
                          key={`${dialog.type}-${dialog.id}`}
                          className={`chat-list-item${dialog.type === 'saved' ? ' chat-list-item--saved' : ''}`}
                          onClick={() => handleSelectDialog(dialog)}
                        >
                          <div className="chat-list-avatar">
                            {dialog.type === 'saved' ? (
                              <span className="chat-list-avatar-saved"><Bookmark size={20} /></span>
                            ) : dialog.avatar ? (
                              <img src={dialog.avatar} alt={dialog.type} />
                            ) : dialog.type === 'user' ? (
                              <span>{((dialog.displayName || dialog.username || '?')[0] || '?').toUpperCase()}</span>
                            ) : (
                              <Folder size={18} />
                            )}
                            {dialog.type === 'user' && dialog.status === 'online' && (
                              <span className="chat-list-online"></span>
                            )}
                          </div>

                          <div className="chat-list-content">
                            <div className="chat-list-row">
                              <h4 className="chat-list-name">
                                {dialog.type === 'saved' ? 'Избранное' : dialog.type === 'user' ? (dialog.displayName || dialog.username) : dialog.name}
                              </h4>
                              {lastMessageTime && (
                                <span className={`chat-list-time ${dialog.unreadCount > 0 ? 'is-unread' : ''}`}>{lastMessageTime}</span>
                              )}
                            </div>
                            <div className="chat-list-row">
                              <p className={`chat-list-preview ${dialog.unreadCount > 0 ? 'is-unread' : ''}`}>
                                {lastMessagePreview || (dialog.type === 'saved' ? 'Сохранённые сообщения' : dialog.type === 'user' && dialog.status === 'online' ? 'в сети' : 'Нет новых сообщений')}
                              </p>
                              <div className="chat-list-meta">
                                {dialog.unreadCount > 0 && (
                                  <span className="chat-list-badge">{dialog.unreadCount}</span>
                                )}
                                {dialog.type === 'user' && (
                                  <button
                                    type="button"
                                    className="chat-list-delete"
                                    title="Удалить диалог"
                                    onClick={(e) => handleDeleteDialogFromList(e, dialog.id, dialog.displayName || dialog.username)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {showContacts && (
                <>
                  {filteredGroups.length > 0 && (
                    <div className="chat-list-section">
                      <div className="chat-list-section-title">Группы</div>
                      {filteredGroups.map(group => (
                        <div
                          key={`group-${group.id}`}
                          className="chat-contact-item"
                          onClick={() => handleSelectGroup(group)}
                        >
                          <div className="chat-contact-avatar">
                            {group.avatar ? (
                              <img src={group.avatar} alt="Group" />
                            ) : (
                              <span>👥</span>
                            )}
                          </div>
                          <div className="chat-contact-info">
                            <div className="chat-contact-name">{group.name}</div>
                            <div className="chat-contact-status">{group.memberCount || 0} участников</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {filteredChannels.length > 0 && (
                    <div className="chat-list-section">
                      <div className="chat-list-section-title">Каналы</div>
                      {filteredChannels.map(channel => (
                        <div
                          key={`channel-${channel.id}`}
                          className="chat-contact-item"
                          onClick={() => handleSelectChannel(channel)}
                        >
                          <div className="chat-contact-avatar">
                            {channel.avatar ? (
                              <img src={channel.avatar} alt="Channel" />
                            ) : (
                              <span>📢</span>
                            )}
                          </div>
                          <div className="chat-contact-info">
                            <div className="chat-contact-name">{channel.name}</div>
                            <div className="chat-contact-status">{channel.is_admin ? 'Администратор' : 'Подписчик'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {filteredUsers.length === 0 && filteredGroups.length === 0 && filteredChannels.length === 0 ? (
                    <div className="empty-state">
                      <p>{searchQuery ? 'Ничего не найдено' : 'Нет доступных контактов'}</p>
                    </div>
                  ) : (
                    <div className="chat-list-section">
                      {filteredUsers.length > 0 && <div className="chat-list-section-title">Контакты</div>}
                      {filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className="chat-contact-item"
                          onClick={() => handleSelectUser(user)}
                        >
                          <div className="chat-contact-avatar">
                            {user.avatar ? (
                              <img src={user.avatar} alt="Avatar" />
                            ) : (
                              <span>{((user.displayName || user.fullName || user.username || '?')[0] || '?').toUpperCase()}</span>
                            )}
                          </div>
                          <div className="chat-contact-info">
                            <div className="chat-contact-name">{user.displayName || user.fullName || user.username}</div>
                            <div className={`chat-contact-status ${user.status === 'online' ? 'is-online' : ''}`}>
                              {user.status === 'online' ? 'в сети' : 'был(а) недавно'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ===== ПАНЕЛЬ РАЗГОВОРА ===== */}
        <div className={`chat-split-conv${showUserList ? ' hidden md:flex' : ''} flex-col`}>
          {hasActiveChat ? (
            <>
              <div className="chat-header">
        <div className="chat-header-left">
          <button
            onClick={handleBackToUserList}
            className="chat-back-button md:hidden"
            title="Вернуться к списку чатов"
            aria-label="Назад к списку чатов"
          >
            <ArrowLeft size={22} />
          </button>

          <button
            type="button"
            className="chat-header-main"
          >
            <div className="chat-header-avatar">
              {selectedUser?.isSaved ? (
                <span className="chat-header-avatar-saved"><Bookmark size={20} /></span>
              ) : chatAvatar ? (
                <img src={chatAvatar} alt="Avatar" />
              ) : selectedGroup ? (
                <span>👥</span>
              ) : selectedChannel ? (
                <span>📢</span>
              ) : (
                <span>
                  {(chatName || '?').toString().trim().charAt(0).toUpperCase() || '?'}
                </span>
              )}
            </div>

            <div className="chat-header-info">
              <div className="chat-name">{chatName}</div>
              {!selectedUser?.isSaved && (
                <div className="chat-status">
                  {selectedUser?.status === 'online' && !selectedChannel && !selectedGroup ? 'в сети' : 'был(а) недавно'}
                </div>
              )}
            </div>
          </button>
        </div>

        <div className="chat-header-actions">
          {(selectedChannel || selectedGroup) && (
            <button
              className="chat-header-action danger"
              onClick={() => {
                if (selectedChannel) {
                  setShowChannelManagementModal(true);
                } else if (selectedGroup) {
                  setShowGroupManagementModal(true);
                }
              }}
              title="Управление"
            >
              <Trash2 size={18} />
            </button>
          )}
          {selectedUser && !selectedChannel && !selectedGroup && !selectedUser?.isSaved && (
            <button
              className="chat-header-action danger"
              onClick={handleDeleteDialog}
              title="Удалить диалог"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="chat-messages" ref={messagesContainerRef}>
        {/* Spacer pushes messages to the bottom when there are few of them */}
        <div className="chat-messages-spacer" />
        {messages.map((message, index) => {
          const showDate = index === 0 || messages[index - 1].date !== message.date;
          return (
            <React.Fragment key={message.id}>
              {showDate && <div className="message-date">{message.date}</div>}
              <div
                className={`message ${message.sender}${selectedMessages.has(message.id) ? ' message-selected' : ''}`}
                data-message-id={message.id}
                onContextMenu={(e) => handleMsgContextMenu(e, message)}
                onClick={() => {
                  if (longPressActivatedRef.current) {
                    // долгий клик мышью только что сработал — игнорируем
                    longPressActivatedRef.current = false;
                    return;
                  }
                  if (selectionMode) handleSelectMessage(message.id);
                }}
                onMouseDown={(e) => handleMouseDown(e, message)}
                onMouseUp={handleMouseUp}
                onMouseLeave={cancelLongPress}
                onTouchStart={(e) => handleTouchStart(e, message)}
                onTouchEnd={(e) => handleTouchEnd(e, message)}
                onTouchMove={handleTouchCancel}
              >
                {message.sender === 'incoming' && (
                  <div className="message-avatar">
                    {message.senderAvatar ? (
                      <img src={message.senderAvatar} alt="Avatar" />
                    ) : (
                      message.senderName?.[0] || '?'
                    )}
                  </div>
                )}
                {message.sender === 'outgoing' && (
                  <div className="message-avatar">
                    {currentUser?.avatar ? (
                      <img src={currentUser.avatar} alt="Avatar" />
                    ) : (
                      <span>{(currentUser?.displayName || currentUser?.username || '?')[0].toUpperCase()}</span>
                    )}
                  </div>
                )}
                <div className="message-content">
                  {message.sender === 'incoming' && (
                    <div className="message-sender-name">{message.senderName}</div>
                  )}
                  {message.replyTo && (
                    <div className="message-reply">
                      <div className="message-reply-content">
                        <div className="message-reply-sender">
                          {message.replyTo.senderName || 'Пользователь'}
                        </div>
                        <div className="message-reply-text">
                          {message.replyTo.text?.substring(0, 50) || 'Сообщение'}
                        </div>
                      </div>
                    </div>
                  )}
                  {(() => {
                    // Текст для карточки: берём message.text, но если он выглядит как
                    // незашифрованный JSON (или messageType=card), пробуем его отдать в SharedCardBubble
                    const cardText = message.text || message.content;
                    const isCard = message.messageType === 'card' ||
                      (typeof cardText === 'string' && cardText.trimStart().startsWith('{') && (() => { try { const p = JSON.parse(cardText); return p && typeof p === 'object' && ('title' in p || 'pageTitle' in p); } catch { return false; } })());

                    const msgBody = isCard ? (
                      <SharedCardBubble messageText={cardText} />
                    ) : message.messageType === 'sticker' ? (
                      <img
                        src={message.image?.startsWith('http') || message.image?.startsWith('/') || message.image?.startsWith('data:')
                          ? message.image
                          : chatApi.getStickerUrl(message.image)}
                        alt="sticker"
                        className="message-sticker"
                        onLoad={handleMessageMediaLoad}
                      />
                    ) : message.image ? (
                      <img
                        src={message.image}
                        alt="Message"
                        className="message-image"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setLightboxImage(message.image)}
                        onLoad={handleMessageMediaLoad}
                      />
                    ) : message.audio ? (
                      <audio controls src={message.audio} className="message-audio" onLoadedMetadata={handleMessageMediaLoad} />
                    ) : (
                      <div className="message-text">{message.text}</div>
                    );

                    if (message.originalSender) {
                      const canScrollToOriginal = message.forwardedFromId &&
                        messages.some(m => String(m.id) === String(message.forwardedFromId));
                      return (
                        <div className="message-forwarded-block">
                          <div
                            className={`message-forwarded-header${canScrollToOriginal ? ' message-forwarded-header--link' : ''}`}
                            onClick={canScrollToOriginal ? (e) => { e.stopPropagation(); scrollToMessage(message.forwardedFromId); } : undefined}
                          >
                            <span className="message-forwarded-arrow">↗</span>
                            <span className="message-forwarded-from">
                              Переслано от {message.originalSender.display_name || message.originalSender.username}
                            </span>
                          </div>
                          {msgBody}
                        </div>
                      );
                    }
                    return msgBody;
                  })()}
                  {message.replyMarkup?.inline_keyboard && (
                    <div className="message-buttons">
                      {(message.replyMarkup.inline_keyboard || []).map((row, rowIndex) => (
                        <div key={rowIndex} className="button-row">
                          {row.map((button, btnIndex) => (
                            <button
                              key={btnIndex}
                              className="message-button"
                              onClick={() => {
                                if (button.callback_data) {
                                  chatWebsocket.sendMessage(
                                    selectedUser?.id || null,
                                    '',
                                    null,
                                    new Date().toISOString(),
                                    {
                                      displayName: currentUser.displayName,
                                      username: currentUser.username,
                                      avatar: currentUser.avatar
                                    },
                                    'text',
                                    null,
                                    null,
                                    null,
                                    null,
                                    null,
                                    button.callback_data
                                  );
                                } else {
                                  handleSendMessage(button.text);
                                }
                              }}
                            >
                              {button.text}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="message-footer">
                    <div className="message-time">
                      {message.editedAt && <span className="message-edited-label">изменено</span>}
                      {message.time}
                    </div>
                  </div>
                  {messageReactions[message.id] && Object.keys(messageReactions[message.id]).length > 0 && (
                    <div className="message-reactions">
                      {Object.entries(messageReactions[message.id]).map(([emoji, reactions]) => (
                        <button
                          key={emoji}
                          className={`reaction-badge ${reactions.some(r => r.userId === currentUser?.id) ? 'reaction-badge-active' : ''}`}
                          onClick={() => handleToggleReaction(message.id, emoji)}
                          title={reactions.map(r => r.user?.displayName || r.user?.username || 'Пользователь').join(', ')}
                        >
                          <span className="reaction-emoji">{emoji}</span>
                          <span className="reaction-count">{reactions.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        {editingMessage && (
          <div className="edit-preview">
            <div className="edit-preview-icon">✏️</div>
            <div className="edit-preview-content">
              <div className="edit-preview-label">Редактирование</div>
              <div className="edit-preview-text">{editingMessage.text?.substring(0, 80)}</div>
            </div>
            <button className="edit-preview-cancel" onClick={handleCancelEdit} title="Отмена">✕</button>
          </div>
        )}
        {!editingMessage && replyingToMessage && (
          <div className="reply-preview">
            <div className="reply-preview-header">
              <span>Ответ на: {replyingToMessage.senderName || 'Сообщение'}</span>
              <button onClick={handleCancelReply}>✕</button>
            </div>
            <div className="reply-preview-content">
              {replyingToMessage.text?.substring(0, 100)}
            </div>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (messageInputRef.current) {
              const text = messageInputRef.current.value.trim();
              if (text) {
                if (editingMessage) {
                  handleSaveEdit(text);
                } else {
                  handleSendMessage(text);
                  messageInputRef.current.value = '';
                }
              }
            }
          }}
        >
          {activeReplyKeyboard?.keyboard && isReplyKeyboardOpen && (
            <div className="reply-keyboard">
              {activeReplyKeyboard.keyboard.map((row, rowIndex) => (
                <div key={rowIndex} className="reply-keyboard-row">
                  {row.map((button, btnIndex) => (
                    <button
                      key={btnIndex}
                      type="button"
                      className="reply-keyboard-button"
                      onClick={() => handleSendMessage(button.text)}
                    >
                      {button.text}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          <div className="chat-input-row" style={{ position: 'relative' }}>
            {showStickerPicker && stickerPacks.length > 0 && (
              <StickerPicker
                packs={stickerPacks}
                onSelect={handleSendSticker}
                onClose={() => setShowStickerPicker(false)}
              />
            )}
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              id="image-input"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleSendImage(file, '');
                }
              }}
            />
            {selectedUser?.isBot && (
              <button
                type="button"
                className="chat-input-icon"
                title={isReplyKeyboardOpen ? 'Скрыть меню' : 'Показать меню'}
                onClick={() => {
                  if (activeReplyKeyboard?.keyboard) {
                    setIsReplyKeyboardOpen(v => !v);
                    return;
                  }
                  handleSendMessage('/start');
                }}
              >
                <Menu size={20} />
              </button>
            )}
            <button
              type="button"
              className="chat-input-icon"
              onClick={() => document.getElementById('image-input').click()}
            >
              <Camera size={20} />
            </button>
            <button
              type="button"
              className={`chat-input-icon${showStickerPicker ? ' chat-input-icon--active' : ''}`}
              title="Стикеры"
              onClick={() => {
                setShowStickerPicker((v) => {
                  const next = !v;
                  if (next) fetchStickerPacks();
                  return next;
                });
              }}
            >
              <Smile size={20} />
            </button>
            <input
              ref={messageInputRef}
              type="text"
              placeholder={editingMessage ? "Изменить сообщение..." : replyingToMessage ? "Ответить..." : "Сообщение"}
              className="chat-input"
              onFocus={() => setShowStickerPicker(false)}
            />
            <button type="submit" className="chat-send-button">
              <Send size={18} />
            </button>
          </div>
        </form>
              </div>
            </>
          ) : (
            <div className="chat-split-empty">
              <p>Выберите чат для начала общения</p>
            </div>
          )}
        </div>
      </div>
      {modals}

      {/* ── Контекстное меню сообщения ───────────────────────────────────────── */}
      {contextMenu && (
        <>
          <div className="ctx-menu-overlay" onClick={closeContextMenu} />
          <div
            ref={contextMenuRef}
            className="ctx-menu"
            style={{ top: contextMenu.y, left: contextMenu.x }}
          >
            {contextMenuReactionOpen ? (
              <div className="ctx-menu-reactions">
                {['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '👏'].map(emoji => (
                  <button
                    key={emoji}
                    className="ctx-menu-reaction-btn"
                    onClick={() => {
                      handleToggleReaction(contextMenu.message.id, emoji);
                      closeContextMenu();
                    }}
                  >
                    {emoji}
                  </button>
                ))}
                <button className="ctx-menu-reaction-back" onClick={() => setContextMenuReactionOpen(false)}>‹</button>
              </div>
            ) : (
              <>
                <button className="ctx-menu-item" onClick={() => setContextMenuReactionOpen(true)}>
                  <span className="ctx-menu-item-icon">😊</span> Реакция
                </button>
                <button className="ctx-menu-item" onClick={() => { handleReply(contextMenu.message); closeContextMenu(); }}>
                  <span className="ctx-menu-item-icon">↩️</span> Ответить
                </button>
                <button className="ctx-menu-item" onClick={() => { handleForwardMessage(contextMenu.message); closeContextMenu(); }}>
                  <span className="ctx-menu-item-icon">↗️</span> Переслать
                </button>
                {contextMenu.message.sender === 'outgoing' && contextMenu.message.messageType === 'text' && !contextMenu.message.originalSender && (
                  <button className="ctx-menu-item" onClick={() => { handleStartEdit(contextMenu.message); closeContextMenu(); }}>
                    <span className="ctx-menu-item-icon">✏️</span> Изменить
                  </button>
                )}
                <button className="ctx-menu-item" onClick={() => {
                  setSelectionMode(true);
                  setSelectedMessages(new Set([contextMenu.message.id]));
                  closeContextMenu();
                }}>
                  <span className="ctx-menu-item-icon">☑️</span> Выбрать
                </button>
                <div className="ctx-menu-divider" />
                {contextMenu.message.sender === 'outgoing' && (
                  <button className="ctx-menu-item ctx-menu-item--danger" onClick={() => { handleDeleteMessage(contextMenu.message.id); closeContextMenu(); }}>
                    <span className="ctx-menu-item-icon">🗑️</span> Удалить
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Панель мультиселекта ──────────────────────────────────────────────── */}
      {selectionMode && (
        <div className="selection-bar">
          <button className="selection-bar-cancel" onClick={exitSelectionMode}>✕</button>
          <span className="selection-bar-count">
            {selectedMessages.size > 0 ? `${selectedMessages.size} выбрано` : 'Выберите сообщения'}
          </span>
          <div className="selection-bar-actions">
            <button
              className="selection-bar-btn"
              disabled={selectedMessages.size === 0}
              onClick={handleBulkForward}
              title="Переслать выбранные"
            >
              ↗️ Переслать
            </button>
            <button
              className="selection-bar-btn selection-bar-btn--danger"
              disabled={selectedMessages.size === 0}
              onClick={handleBulkDelete}
              title="Удалить выбранные"
            >
              🗑️ Удалить
            </button>
          </div>
        </div>
      )}

      {/* Forward Message Modal */}
      {showForwardModal && forwardingMessage && (
        <div className="modal-overlay" onClick={() => setShowForwardModal(false)}>
          <div className="modal-content forward-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Переслать сообщение</h3>
              <button className="modal-close" onClick={() => setShowForwardModal(false)}>✕</button>
            </div>
            <div className="forward-modal-body">
              <div className="forward-message-preview">
                <div className="forward-message-preview-header">
                  {forwardingMessage.originalSender && (
                    <div className="forward-message-preview-forwarded">
                      Переслано от {forwardingMessage.originalSender.display_name || forwardingMessage.originalSender.username}
                    </div>
                  )}
                  {forwardingMessage.text && (
                    <div className="forward-message-preview-text">{forwardingMessage.text}</div>
                  )}
                  {forwardingMessage.image && (
                    <img src={forwardingMessage.image} alt="Preview" className="forward-message-preview-image" />
                  )}
                </div>
              </div>
              <div className="forward-recipients">
                <div className="forward-section">
                  <h4>Контакты</h4>
                  <div className="forward-list">
                    {users.map(user => (
                      <label key={user.id} className="forward-item">
                        <input
                          type="checkbox"
                          value={user.id}
                          onChange={(e) => {
                            // Handle selection
                          }}
                        />
                        <div className="forward-item-avatar">
                          {user.avatar ? (
                            <img src={user.avatar} alt="Avatar" />
                          ) : (
                            <span>{((user.displayName || user.username || '?')[0] || '?').toUpperCase()}</span>
                          )}
                        </div>
                        <div className="forward-item-name">
                          {user.displayName || user.username}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="forward-section">
                  <h4>Группы</h4>
                  <div className="forward-list">
                    {groups.map(group => (
                      <label key={group.id} className="forward-item">
                        <input
                          type="checkbox"
                          value={group.id}
                          onChange={(e) => {
                            // Handle selection
                          }}
                        />
                        <div className="forward-item-avatar">
                          {group.avatar ? (
                            <img src={group.avatar} alt="Group" />
                          ) : (
                            <span>👥</span>
                          )}
                        </div>
                        <div className="forward-item-name">{group.name}</div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-button secondary" onClick={() => setShowForwardModal(false)}>
                Отмена
              </button>
              <button
                className="modal-button primary"
                onClick={() => {
                  const selectedUsers = Array.from(document.querySelectorAll('.forward-item input[type="checkbox"]:checked'))
                    .filter(cb => users.some(u => u.id === parseInt(cb.value)))
                    .map(cb => parseInt(cb.value));
                  const selectedGroups = Array.from(document.querySelectorAll('.forward-item input[type="checkbox"]:checked'))
                    .filter(cb => groups.some(g => g.id === parseInt(cb.value)))
                    .map(cb => parseInt(cb.value));
                  handleConfirmForward(selectedUsers, selectedGroups);
                }}
              >
                Переслать
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IntegratedChatScreen;
